import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { useTranslation } from "@/features/utils/i18n";
import type { BookingTarget } from "../types";

/**
 * Where an appointment created by hand is booked: the company's booking system, an
 * agent's own, or the internal calendar only. Rendered inside a `FormField`.
 */
export function BookingTargetField({
  targets,
  value,
  onChange,
}: {
  targets: BookingTarget[];
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation("calendar");
  const integrationName = useIntegrationName();

  const label = (target: BookingTarget) => {
    if (target.id === "internal") return t("form.bookingTargets.internal");
    const integration = integrationName(target.integration);
    return target.agent_id
      ? t("form.bookingTargets.agent", { integration, name: target.name })
      : t("form.bookingTargets.default", { integration });
  };

  return (
    <FormItem>
      <FormLabel>{t("form.bookingTarget")}</FormLabel>
      <Select value={value} onValueChange={onChange}>
        <FormControl>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {targets.map((target) => (
            <SelectItem key={target.id} value={target.id}>
              {label(target)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FormDescription>{t("form.bookingTargetHint")}</FormDescription>
      <FormMessage />
    </FormItem>
  );
}
